/**
 * @file WebGL renderer wrapper.
 *
 * Owns the Three.js `WebGLRenderer` and the canvas resize contract, and nothing
 * else. No game rule ever lands here (GDD §25).
 */

import * as THREE from 'three';
import { clamp } from '../utils/MathUtils.js';

export class Renderer {
  /**
   * @param {HTMLCanvasElement} canvas Target canvas.
   * @param {import('../config/GameConfig.js').GameConfig} config Game config.
   */
  constructor(canvas, config) {
    /** @type {HTMLCanvasElement} */
    this.canvas = canvas;
    /** @type {import('../config/GameConfig.js').GameConfig} */
    this.config = config;

    const settings = config.renderer;

    /** @type {THREE.WebGLRenderer} */
    this.instance = new THREE.WebGLRenderer({
      canvas,
      antialias: settings.antialias,
      powerPreference: 'high-performance',
      stencil: false
    });

    this.instance.setClearColor(settings.clearColor, 1);
    this.instance.outputColorSpace = THREE.SRGBColorSpace;
    this.instance.toneMapping = THREE.ACESFilmicToneMapping;
    this.instance.toneMappingExposure = 1.05;

    if (settings.shadows) {
      this.instance.shadowMap.enabled = true;
      this.instance.shadowMap.type = THREE.PCFSoftShadowMap;
    }

    /** @type {() => void} @private */
    this._onResize = () => this.resize();
    window.addEventListener('resize', this._onResize);
    this.resize();
  }

  /**
   * Matches the drawing buffer to the CSS size of the canvas.
   * Pixel ratio is capped so high-DPI screens do not tank the frame budget.
   * @returns {void}
   */
  resize() {
    const { width, height } = this.size;
    const ratio = clamp(window.devicePixelRatio, 1, this.config.renderer.maxPixelRatio);

    this.instance.setPixelRatio(ratio);
    this.instance.setSize(width, height, false);
  }

  /**
   * Current viewport size in CSS pixels, never zero.
   *
   * A hidden or minimised window reports a client size of 0. Propagating that
   * produces an aspect ratio of 0, which makes the projection matrix singular
   * and turns every unprojected ray into NaN — silently breaking mouse aiming
   * with no way to recover once the window comes back. Clamping here is the
   * single choke point that prevents it.
   *
   * @returns {{ width: number, height: number }}
   */
  get size() {
    return {
      width: Math.max(1, this.canvas.clientWidth || window.innerWidth || 1),
      height: Math.max(1, this.canvas.clientHeight || window.innerHeight || 1)
    };
  }

  /** @returns {number} Current drawing-buffer aspect ratio, always positive. */
  get aspect() {
    const { width, height } = this.size;
    return width / height;
  }

  /**
   * Draws one frame.
   * @param {THREE.Scene} scene Scene to draw.
   * @param {THREE.Camera} camera Active camera.
   * @returns {void}
   */
  render(scene, camera) {
    this.instance.render(scene, camera);
  }

  /**
   * Releases GPU resources and listeners.
   * @returns {void}
   */
  dispose() {
    window.removeEventListener('resize', this._onResize);
    this.instance.dispose();
  }
}
